import type {
  ModelTier,
  SignalReason,
} from '../domain/types'
import type { SemanticRouteDecision } from './semanticRouting'

export type ConservativePreviewTier = Extract<
  ModelTier,
  'standard' | 'deep'
>

export type SemanticConfirmationStatus =
  | 'confirmed'
  | 'abstained'
  | 'disagreement'
  | 'deterministic-floor'

export type SemanticConfirmationReason =
  | 'semantic-confirmed-direct'
  | 'semantic-abstained'
  | 'semantic-disagreement'
  | 'deterministic-standard-floor'
  | 'deterministic-deep-floor'

export type SemanticConfirmationInput = {
  deterministicTier: ModelTier
  conservativePreviewTier: ConservativePreviewTier
  semanticDecision: SemanticRouteDecision
}

export type SemanticConfirmationResult = {
  tier: ModelTier
  status: SemanticConfirmationStatus
  reason: SemanticConfirmationReason
  deterministicTier: ModelTier
  conservativePreviewTier: ConservativePreviewTier
  semanticTier?: ModelTier
  semanticAbstentionReason?: SemanticRouteDecision['abstentionReason']
}

function assertConservativePreviewTier(
  tier: ModelTier,
): asserts tier is ConservativePreviewTier {
  if (tier !== 'standard' && tier !== 'deep') {
    throw new Error(
      'The conservative preview tier must be Standard or Deep.',
    )
  }
}

/**
 * Applies semantic evidence as a one-way confirmation gate.
 *
 * Semantics may unlock Direct only when both classifiers independently
 * choose Direct. Every deterministic Standard or Deep decision is final,
 * and semantic disagreement or abstention cannot move it in either
 * direction.
 */
export function applySemanticConfirmationPolicy({
  deterministicTier,
  conservativePreviewTier,
  semanticDecision,
}: SemanticConfirmationInput): SemanticConfirmationResult {
  assertConservativePreviewTier(conservativePreviewTier)

  const audit = {
    deterministicTier,
    conservativePreviewTier,
    semanticTier: semanticDecision.tier,
    semanticAbstentionReason:
      semanticDecision.abstentionReason,
  }

  if (deterministicTier === 'standard') {
    return {
      ...audit,
      tier: 'standard',
      status: 'deterministic-floor',
      reason: 'deterministic-standard-floor',
    }
  }

  if (deterministicTier === 'deep') {
    return {
      ...audit,
      tier: 'deep',
      status: 'deterministic-floor',
      reason: 'deterministic-deep-floor',
    }
  }

  if (
    !semanticDecision.abstained &&
    semanticDecision.tier === 'direct'
  ) {
    return {
      ...audit,
      tier: 'direct',
      status: 'confirmed',
      reason: 'semantic-confirmed-direct',
    }
  }

  if (
    semanticDecision.abstained ||
    semanticDecision.tier === undefined
  ) {
    return {
      ...audit,
      tier: conservativePreviewTier,
      status: 'abstained',
      reason: 'semantic-abstained',
    }
  }

  return {
    ...audit,
    tier: conservativePreviewTier,
    status: 'disagreement',
    reason: 'semantic-disagreement',
  }
}

export function semanticConfirmationSignalReason(
  result: SemanticConfirmationResult,
): SignalReason {
  if (result.status === 'confirmed') {
    return {
      id: 'semantic-confirmation-direct',
      label: 'Local match confirmed Direct',
      detail:
        'The deterministic and local semantic checks independently selected the Direct tier.',
      impact: 'down',
    }
  }

  if (result.status === 'abstained') {
    return {
      id: 'semantic-confirmation-abstained',
      label: 'Local check abstained',
      detail:
        'The semantic evidence did not clear the frozen confidence threshold, so the conservative capability floor remains.',
      impact: 'risk',
    }
  }

  if (result.status === 'disagreement') {
    return {
      id: 'semantic-confirmation-disagreement',
      label: 'Checks disagreed',
      detail:
        'The deterministic and local semantic checks selected different tiers, so the conservative capability floor remains.',
      impact: 'risk',
    }
  }

  return {
    id: `semantic-confirmation-${result.deterministicTier}-floor`,
    label: 'Deterministic capability floor',
    detail:
      'Local semantic evidence can confirm Direct work, but it cannot lower a deterministic Standard or Deep route.',
    impact: 'risk',
  }
}
